import type { FileAttachmentItem } from '@ophelos/ui/components/file-attachments';
import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';
import { useAuth } from '@/contexts/customer-auth-context';
import type { PortalPageRequest } from '@/lib/portalNavigation';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  attachments?: FileAttachmentItem[];
  /** The pages the agent offered with this reply, each linked when the portal can resolve it. */
  pages?: PortalPageRequest[];
  /** On the agent's reply in a turn it made a handoff: the customer message it answers. */
  handoffReplyTo?: string;
}

/** What the customer asked to be handed over, and when they sent it. */
export interface HandoffRequest {
  message: string;
  sentAt: Date;
}

/** What the customer asked the agent to hand over to the team. */
export interface ChatHandoff extends HandoffRequest {
  /** The customer's message the handoff was made on, often their yes to the agent's offer. */
  messageId: string;
}

export interface ChatConversation {
  accountId: string | null;
  /** The AgentOS session; null until the customer is logged in. */
  sessionId: string | null;
  messages: ChatMessage[];
  /** The first handoff in this conversation; the server allows one at a time. */
  handoff: ChatHandoff | null;
  /** What the first handoff that failed was for, so the retry that works quotes it rather than the retry. */
  failedHandoffRequest: HandoffRequest | null;
}

const newConversation = (accountId: string | null): ChatConversation => ({
  accountId,
  sessionId: accountId ? `session_${accountId}_${Date.now()}` : null,
  messages: [],
  handoff: null,
  failedHandoffRequest: null,
});

interface AIContextValue {
  isOpen: boolean;
  defaultSize: number;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  onLayoutChange: (layout: { [id: string]: number }) => void;
  conversation: ChatConversation;
  /** Adds to the conversation `sessionId` belongs to — dropped if that one has been replaced. */
  appendMessage: (sessionId: string | null, message: ChatMessage) => void;
  markHandedOver: (sessionId: string | null, handoff: ChatHandoff) => void;
  markHandoffFailed: (sessionId: string | null, request: HandoffRequest) => void;
}

const AIContext = createContext<AIContextValue | undefined>(undefined);

const AI_PANEL_STORAGE_KEY = 'ai-panel-state';
const DEFAULT_SIZE = 25; // Default percentage of screen (25%)

interface AIContextProviderProps {
  children: ReactNode;
}

export function AIContextProvider({ children }: AIContextProviderProps) {
  const [isOpen, setIsOpen] = useState(() => {
    const stored = localStorage.getItem(AI_PANEL_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed.isOpen ?? false;
      } catch {
        return false;
      }
    }
    return false;
  });

  const [defaultSize] = useState(() => {
    const stored = localStorage.getItem(AI_PANEL_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed.defaultSize ?? DEFAULT_SIZE;
      } catch {
        return DEFAULT_SIZE;
      }
    }
    return DEFAULT_SIZE;
  });

  // Held here rather than in the panel, which unmounts when it closes (and on mobile when a
  // page button collapses it). Reloading still starts a fresh chat: nothing is persisted.
  const { accountId } = useAuth();
  const [conversation, setConversation] = useState(() => newConversation(accountId));
  // Replaced during render, so React re-renders before committing: no frame shows, or writes
  // to, another account's conversation.
  if (conversation.accountId !== accountId) setConversation(newConversation(accountId));

  const appendMessage = useCallback((sessionId: string | null, message: ChatMessage) => {
    setConversation((prev) => (prev.sessionId === sessionId ? { ...prev, messages: [...prev.messages, message] } : prev));
  }, []);

  const markHandedOver = useCallback((sessionId: string | null, handoff: ChatHandoff) => {
    setConversation((prev) => (prev.sessionId === sessionId && !prev.handoff ? { ...prev, handoff } : prev));
  }, []);

  const markHandoffFailed = useCallback((sessionId: string | null, request: HandoffRequest) => {
    setConversation((prev) => (prev.sessionId === sessionId && !prev.failedHandoffRequest ? { ...prev, failedHandoffRequest: request } : prev));
  }, []);

  const openPanel = () => {
    setIsOpen(true);
    localStorage.setItem(AI_PANEL_STORAGE_KEY, JSON.stringify({ isOpen: true, defaultSize }));
  };

  const closePanel = () => {
    setIsOpen(false);
    localStorage.setItem(AI_PANEL_STORAGE_KEY, JSON.stringify({ isOpen: false, defaultSize }));
  };

  const togglePanel = () => {
    setIsOpen((prev: boolean) => {
      const newIsOpen = !prev;
      localStorage.setItem(AI_PANEL_STORAGE_KEY, JSON.stringify({ isOpen: newIsOpen, defaultSize }));
      return newIsOpen;
    });
  };

  // Callback for ResizablePanelGroup to update sizes
  const onLayoutChange = (layout: { [id: string]: number }) => {
    const aiPanelSize = layout['ai-panel'];
    if (aiPanelSize !== undefined) {
      const stored = localStorage.getItem(AI_PANEL_STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          localStorage.setItem(AI_PANEL_STORAGE_KEY, JSON.stringify({ ...parsed, defaultSize: aiPanelSize }));
        } catch {
          localStorage.setItem(AI_PANEL_STORAGE_KEY, JSON.stringify({ isOpen, defaultSize: aiPanelSize }));
        }
      }
    }
  };

  const value: AIContextValue = {
    isOpen,
    defaultSize,
    openPanel,
    closePanel,
    togglePanel,
    onLayoutChange,
    conversation,
    appendMessage,
    markHandedOver,
    markHandoffFailed,
  };

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
}

export function useAIPanel() {
  const context = useContext(AIContext);
  if (context === undefined) {
    throw new Error('useAIPanel must be used within AIContextProvider');
  }
  return context;
}
